import ViewOOPModal from '@/components/sharedModals/ViewOOPModal';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import ConfirmationDialog from '@/components/ui/ConfirmationDialog';
import { TableCell, TableRow } from "@/components/ui/table";
import {
   Tooltip,
   TooltipContent,
   TooltipProvider,
   TooltipTrigger,
} from "@/components/ui/tooltip";
import useActionHotkeys from '@/hooks/useActionHotkeys';
import useEscapeKey from '@/hooks/useEscapeKey';
import { gql, useMutation, useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { Eye, FileCheck2, FileText, Receipt, RotateCcw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import BC_ViewORModal from './BC_ViewORModal';
import GenerateORModal from './GenerateORModal';
import ManualPaymentConfirmDialog from './ManualPaymentConfirmDialog';
import ReviewPaymentModal from './ReviewPaymentModal';

const GET_PAYMENT_BY_OOP = gql`
   query GetPaymentByOOP($oopId: ID!) {
      getPayments(oopId: $oopId) {
         id
         oopId
         status
         amount
         paymentMethod
         paymentDetails {
            fullName
            email
            phoneNumber
            address
         }
         createdAt
      }
   }
`;

const UNDO_MANUAL_PAYMENT = gql`
   mutation UndoManualPayment($paymentId: ID!) {
      undoManualPayment(paymentId: $paymentId) {
         success
         message
         paymentStatus
      }
   }
`;

const UNDO_ISSUED_OR = gql`
   mutation UndoIssuedOR($oopId: ID!) {
      undoIssuedOR(oopId: $oopId) {
         success
         message
         oop {
            _id
            OOPstatus
         }
      }
   }
`;

const BC_OOPRow = ({ oop, onReviewComplete, currentTab, isMobile }) => {
   const [isViewModalOpen, setIsViewModalOpen] = useState(false);
   const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
   const [isGenerateORModalOpen, setIsGenerateORModalOpen] = useState(false);
   const [showConfirmDialog, setShowConfirmDialog] = useState(false);
   const [showUndoORDialog, setShowUndoORDialog] = useState(false);
   const [isViewORModalOpen, setIsViewORModalOpen] = useState(false);

   const { data: paymentData } = useQuery(GET_PAYMENT_BY_OOP, {
      variables: { oopId: oop._id },
      skip: !oop?._id
   });

   const payment = useMemo(() => {
      return paymentData?.getPayments?.find(p => p.oopId === oop._id);
   }, [paymentData, oop._id]);

   const [undoManualPayment] = useMutation(UNDO_MANUAL_PAYMENT, {
      onCompleted: (data) => {
         if (data.undoManualPayment.success) {
            toast.success('Manual payment has been reset');
            onReviewComplete();
         }
      },
      onError: (error) => {
         toast.error(`Failed to undo manual payment: ${error.message}`);
      }
   });

   const [undoIssuedOR] = useMutation(UNDO_ISSUED_OR, {
      onCompleted: (data) => {
         if (data.undoIssuedOR.success) {
            toast.success('Official Receipt has been removed');
            onReviewComplete();
         }
      },
      onError: (error) => {
         toast.error(`Failed to undo issued OR: ${error.message}`);
      }
   });

   const handleViewClick = () => setIsViewModalOpen(true);
   const handleReviewClick = () => setIsReviewModalOpen(true);
   const handleGenerateOR = () => setIsGenerateORModalOpen(true);
   const handleViewORClick = () => setIsViewORModalOpen(true);

   const handleReviewComplete = () => {
      setIsReviewModalOpen(false);
      onReviewComplete();
   };

   const handleORComplete = () => {
      setIsGenerateORModalOpen(false);
      onReviewComplete();
   };

   const handleConfirmPayment = () => {
      setShowConfirmDialog(true);
   };

   const handleUndoManualPayment = async () => {
      if (payment?.id) {
         await undoManualPayment({
            variables: { paymentId: payment.id }
         });
      }
   };

   const handleUndoIssuedOR = () => {
      setShowUndoORDialog(true);
   };

   const handleConfirmUndoOR = async () => {
      try {
         await undoIssuedOR({
            variables: { oopId: oop._id }
         });
         setShowUndoORDialog(false);
      } catch (error) {
         console.error('Error undoing OR:', error);
      }
   };

   const actions = [
      {
         icon: Eye,
         label: "View OOP",
         onClick: handleViewClick,
         variant: "outline",
         className: "view-oop-action",
         hotkey: "Ctrl + 1"
      }
   ];

   if (oop.OOPstatus === 'Awaiting Manual Payment') {
      actions.push({
         icon: FileCheck2,
         label: "Confirm Manual Payment",
         onClick: handleConfirmPayment,
         variant: "outline",
         className: "confirm-payment-action text-green-600 hover:text-green-800",
         hotkey: "Ctrl + 2"
      });
   }

   if (currentTab === 'Payment Proof') {
      actions.push({
         icon: FileCheck2,
         label: "Review Payment",
         onClick: handleReviewClick,
         variant: "outline",
         className: "review-payment-action text-blue-600 hover:text-blue-800",
         hotkey: "Ctrl + 3"
      });
   }

   if (currentTab === 'Completed Payments') {
      actions.push({
         icon: FileText,
         label: "Generate OR",
         onClick: handleGenerateOR,
         variant: "outline",
         className: "generate-or-action text-green-600 hover:text-green-800",
         hotkey: "Ctrl + 4"
      });
   }

   if (currentTab === 'Issued OR') {
      actions.push({
         icon: Receipt,
         label: "View OR",
         onClick: handleViewORClick,
         variant: "outline",
         className: "text-blue-600 hover:text-blue-800",
         hotkey: "Ctrl + 4"
      });

      actions.push({
         icon: RotateCcw,
         label: "Undo Issued OR",
         onClick: handleUndoIssuedOR,
         variant: "outline",
         className: "text-yellow-600 hover:text-yellow-800",
         hotkey: "Ctrl + 5"
      });
   }

   useActionHotkeys({
      actions,
      activeMainTab: currentTab,
      disabled: isViewModalOpen || isReviewModalOpen || isGenerateORModalOpen ||
         showConfirmDialog || showUndoORDialog || isViewORModalOpen
   });

   useEscapeKey(() => setIsViewModalOpen(false), isViewModalOpen);
   useEscapeKey(() => setIsReviewModalOpen(false), isReviewModalOpen);
   useEscapeKey(() => setIsGenerateORModalOpen(false), isGenerateORModalOpen);
   useEscapeKey(() => setShowConfirmDialog(false), showConfirmDialog);
   useEscapeKey(() => setShowUndoORDialog(false), showUndoORDialog);
   useEscapeKey(() => setIsViewORModalOpen(false), isViewORModalOpen);

   const getStatusBadgeColor = (status) => {
      switch (status.toLowerCase()) {
         case 'awaiting manual payment':
            return {
               variant: 'warning',
               className: 'bg-purple-100 text-purple-800 hover:bg-purple-100/80'
            };
         case 'payment proof submitted':
            return {
               variant: 'warning',
               className: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100/80'
            };
         case 'payment proof approved':
            return {
               variant: 'success',
               className: 'bg-green-100 text-green-800 hover:bg-green-100/80'
            };
         case 'awaiting payment':
            return {
               variant: 'default',
               className: 'bg-blue-100 text-blue-800 hover:bg-blue-100/80'
            };
         case 'issued or':
            return {
               variant: 'success',
               className: 'bg-green-100 text-green-800 hover:bg-green-100/80'
            };
         default:
            return {
               variant: 'secondary',
               className: 'bg-gray-100 text-gray-800 hover:bg-gray-100/80'
            };
      }
   };

   const formatDate = (timestamp) => {
      const date = new Date(parseInt(timestamp));
      return format(date, 'MMM d, yyyy');
   };

   return (
      <>
         <TableRow>
            <TableCell>{oop.applicationNumber}</TableCell>
            <TableCell>{oop.billNo}</TableCell>
            <TableCell>{formatDate(oop.createdAt)}</TableCell>
            <TableCell>₱{oop.totalAmount.toFixed(2)}</TableCell>
            <TableCell>
               <Badge
                  variant={getStatusBadgeColor(oop.OOPstatus).variant}
                  className={getStatusBadgeColor(oop.OOPstatus).className}
               >
                  {oop.OOPstatus}
               </Badge>
            </TableCell>
            <TableCell className="text-right">
               <div className="flex justify-end gap-2 table-actions">
                  {actions.map((action, index) => (
                     <TooltipProvider key={index} delayDuration={200}>
                        <Tooltip>
                           <TooltipTrigger asChild>
                              <Button
                                 variant={action.variant}
                                 size="icon"
                                 onClick={action.onClick}
                                 className={action.className}
                              >
                                 <action.icon className="h-4 w-4" />
                              </Button>
                           </TooltipTrigger>
                           <TooltipContent>
                              <p>{action.label} {action.hotkey ? `(${action.hotkey})` : ''}</p>
                           </TooltipContent>
                        </Tooltip>
                     </TooltipProvider>
                  ))}
               </div>
            </TableCell>
         </TableRow>

         {/* Modals */}
         <ViewOOPModal
            isOpen={isViewModalOpen}
            onClose={() => setIsViewModalOpen(false)}
            oop={oop}
         />
         <ReviewPaymentModal
            isOpen={isReviewModalOpen}
            onClose={() => setIsReviewModalOpen(false)}
            oop={oop}
            onReviewComplete={handleReviewComplete}
         />
         <GenerateORModal
            isOpen={isGenerateORModalOpen}
            onClose={() => setIsGenerateORModalOpen(false)}
            oop={oop}
            onComplete={handleORComplete}
         />
         <ManualPaymentConfirmDialog
            isOpen={showConfirmDialog}
            onClose={() => setShowConfirmDialog(false)}
            oop={oop}
            onConfirm={onReviewComplete}
         />
         <ConfirmationDialog
            isOpen={showUndoORDialog}
            onClose={() => setShowUndoORDialog(false)}
            onConfirm={handleConfirmUndoOR}
            title="Remove Official Receipt"
            description="Are you sure you want to remove this Official Receipt? This action will revert the OOP status back to 'Completed OOP'."
            confirmText="Remove OR"
            variant="destructive"
         />
         <BC_ViewORModal
            isOpen={isViewORModalOpen}
            onClose={() => setIsViewORModalOpen(false)}
            oop={oop}
         />
      </>
   );
};

export default BC_OOPRow;
